import React, { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { Form } from '@/shared/components';
import type { DerivativeNameSummary } from '@/features/vehiclesearch/services/lookup';
import { usePlanVehicleRuleCascade } from '../hooks/usePlanVehicleRuleCascade';
import { usePlanVehicleRuleOptionState } from '../hooks/usePlanVehicleRuleOptionState';
import { useVehicleRestrictionOptions } from '../hooks/useVehicleRestrictionOptions';
import { planVehicleRuleSchema } from '../schemas/planVehicleRule.schema';
import type { PlanVehicleRuleFormInput, PlanVehicleRuleFormOutput } from '../schemas/planVehicleRule.schema';
import type { PlanVehicleRule, PlanVehicleRulePayload } from '../types/planVehicleRule.types';
import { toRequires4x4 } from '../utils/fourByFourChoice';
import { buildEditState, emptyState, toSelectOptions } from './planVehicleRuleFormState';
import { PlanVehicleRuleVehicleFields } from './PlanVehicleRuleVehicleFields';
import { PlanVehicleRuleHints } from './PlanVehicleRuleHints';
import { PlanVehicleRuleEngineFields } from './PlanVehicleRuleEngineFields';
import { PlanVehicleRuleAgeFields } from './PlanVehicleRuleAgeFields';
import { PlanVehicleRuleFormActions } from './PlanVehicleRuleFormActions';

interface Props {
    mode: 'create' | 'edit';
    rule?: PlanVehicleRule;
    derivative?: DerivativeNameSummary;
    onSubmit: (payload: PlanVehicleRulePayload) => void;
    isLoading: boolean;
    error?: string | null;
    onCancel: () => void;
    /** Lets the panel guard row switching so unsaved edits are not silently discarded. */
    onDirtyChange?: (isDirty: boolean) => void;
}

export const PlanVehicleRuleForm: React.FC<Props> = ({ mode, rule, derivative, onSubmit, isLoading, error, onCancel, onDirtyChange }) => {
    const seed = useMemo(
        () => (mode === 'edit' && rule ? buildEditState(rule, derivative) : emptyState),
        // Computed once per mount; the panel keys the form by rule id so a different rule remounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { isSubmitting, isDirty },
    } = useForm<PlanVehicleRuleFormInput, unknown, PlanVehicleRuleFormOutput>({
        resolver: zodResolver(planVehicleRuleSchema),
        defaultValues: seed.defaultValues,
    });

    const {
        filters,
        availableOptions,
        matchingDerivatives,
        isLoadingOptions,
        optionsError: cascadeOptionsError,
        handleFilterChange,
        clearCascade,
        replaySeedCascade,
    } = usePlanVehicleRuleCascade({ seedFilters: seed.filters, mode, setValue });

    const hasMake = Boolean(filters.make_id);
    const hasMakeAndModel = Boolean(filters.make_id && filters.model_id);

    const {
        fuelTypesQuery,
        transmissionsQuery,
        optionsError,
        cascadeOptions,
        modelValue,
        derivativeOptions: derivativeSelectOptions,
        derivativeSelected,
        derivativeValue,
        showNoMatchHint,
    } = usePlanVehicleRuleOptionState({
        control,
        filters,
        availableOptions,
        matchingDerivatives,
        isLoadingOptions,
        cascadeOptionsError,
        hasMakeAndModel,
    });

    const fuelTypeOptions = useMemo(() => toSelectOptions(fuelTypesQuery.data), [fuelTypesQuery.data]);
    const transmissionOptions = useMemo(() => toSelectOptions(transmissionsQuery.data), [transmissionsQuery.data]);

    const { transmissionSelectOptions, fuelTypeSelectOptions } = useVehicleRestrictionOptions({
        transmissionOptions,
        fuelTypeOptions,
        savedTransmissionId: seed.defaultValues.transmission_id ?? '',
        savedFuelTypeId: seed.defaultValues.fuel_type_id ?? '',
        isLoadingTransmissions: transmissionsQuery.isLoading,
        isLoadingFuelTypes: fuelTypesQuery.isLoading,
    });

    const makeValue = useMemo(
        () => cascadeOptions.makes.find((o) => o.value === filters.make_id) ?? null,
        [cascadeOptions.makes, filters.make_id],
    );

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
        if (derivativeSelected) {
            setValue('model_id', '');
            setValue('transmission_id', '');
            setValue('fuel_type_id', '');
            setValue('engine_min', '');
            setValue('engine_max', '');
            setValue('age_min', '');
            setValue('age_max', '');
            setValue('requires_4x4', '');
        } else {
            // Derivative cleared: restore the retained navigation state so the submitted payload
            // matches what the reappearing selects display — symmetric with model_id. On first
            // render this runs against the synchronously-seeded `filters` above, so an edit-mode
            // prefill is never clobbered (design section 5.2).
            setValue('model_id', filters.model_id ?? '');
            setValue('transmission_id', filters.transmission_id ?? '');
            setValue('fuel_type_id', filters.fuel_type_id ?? '');
        }
    }, [derivativeSelected, filters.model_id, filters.transmission_id, filters.fuel_type_id, setValue]);

    const handleFormSubmit = (data: PlanVehicleRuleFormOutput) => {
        onSubmit({
            make_id: data.make_id || null,
            model_id: data.model_id || null,
            derivative_id: data.derivative_id || null,
            transmission_id: data.transmission_id || null,
            fuel_type_id: data.fuel_type_id || null,
            engine_min: data.engine_min ?? null,
            engine_max: data.engine_max ?? null,
            age_min: data.age_min ?? null,
            age_max: data.age_max ?? null,
            requires_4x4: toRequires4x4(data.requires_4x4),
        });
    };

    const handleClearForm = () => {
        reset();
        clearCascade();
    };

    const handleReset = () => {
        reset(seed.defaultValues);
        replaySeedCascade();
    };

    const busy = isSubmitting || isLoading;

    // Subscribes to the whole form rather than one named watch per field; true exactly when
    // every field is '' / undefined / null, mode-agnostic by design.
    const watchedValues = useWatch({ control });
    const isRuleEmpty = Object.values(watchedValues).every(
        (v) => v === '' || v === undefined || v === null,
    );

    return (
        <Form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
            <PlanVehicleRuleVehicleFields
                control={control}
                derivativeOptions={cascadeOptions}
                derivativeSelectOptions={derivativeSelectOptions}
                transmissionOptions={transmissionSelectOptions}
                fuelTypeOptions={fuelTypeSelectOptions}
                makeValue={makeValue}
                modelValue={modelValue}
                derivativeValue={derivativeValue}
                filters={filters}
                onFilterChange={handleFilterChange}
                matchingDerivativeCount={matchingDerivatives.length}
                derivativeSelected={derivativeSelected}
                hasMake={hasMake}
                hasMakeAndModel={hasMakeAndModel}
                isLoadingOptions={isLoadingOptions}
                isLoadingTransmissions={transmissionsQuery.isLoading}
                isLoadingFuelTypes={fuelTypesQuery.isLoading}
                busy={busy}
            />

            <PlanVehicleRuleEngineFields control={control} derivativeSelected={derivativeSelected} busy={busy} />

            <PlanVehicleRuleAgeFields control={control} derivativeSelected={derivativeSelected} busy={busy} />

            <PlanVehicleRuleHints isRuleEmpty={isRuleEmpty} showNoMatchHint={showNoMatchHint} />

            <PlanVehicleRuleFormActions
                mode={mode}
                optionsError={optionsError}
                error={error}
                isLoading={isLoading}
                busy={busy}
                onSecondaryAction={mode === 'edit' ? handleReset : handleClearForm}
                onCancel={onCancel}
            />
        </Form>
    );
};
